import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaCog } from 'react-icons/fa';
import {
  fetchMonthlyCampaignProfiles,
  downloadCsv,
  MonthlyCampaignProfilesResponse,
  MonthlyCampaignProfileRow,
} from '~/services/predefinedReports';
import { PredefinedResultsLayout, Column } from './PredefinedResultsLayout';

type ConfigurableColumn = Column<MonthlyCampaignProfileRow> & {
  key: string;
  default?: boolean;
};

const ALL_COLUMNS: ConfigurableColumn[] = [
  { key: 'id', label: 'ID', default: true },
  { key: 'full_name', label: 'Full name', default: true },
  { key: 'first_name', label: 'First name', default: true },
  { key: 'middle_name', label: 'Middle name', default: true },
  { key: 'last_name', label: 'Last name', default: true },
  { key: 'email', label: 'Email', default: true },
  { key: 'city', label: 'City', default: true },
  { key: 'state', label: 'State', default: true },
  { key: 'status', label: 'Status', default: true },
  { key: 'funded_date', label: 'Open', default: true },
  { key: 'fund', label: 'Fund', default: true },
  { key: 'medical_reason', label: 'Medical reason', default: true },
  { key: 'primary_medical_issue', label: 'Primary issue', default: true },
  { key: 'expenses', label: 'Expenses', default: true },
  { key: 'gender', label: 'Gender', default: true },
  { key: 'goal', label: 'Goal', default: true },
  {
    key: 'optional_photo_consent',
    label: 'Optional photo consent',
    default: true,
    render: (r) => (r.optional_photo_consent ? 'Yes' : 'No'),
  },
  {
    key: 'picture',
    label: 'Picture',
    default: true,
    render: (r) =>
      r.picture ? (
        <a href={r.picture} target='_blank' rel='noopener noreferrer'>
          <img src={r.picture} alt='Campaign' className='h-12 w-auto rounded' />
        </a>
      ) : null,
  },
  { key: 'csc_email', label: 'CSC Email', default: true },
  { key: 'preferred_language', label: 'Preferred Language', default: true },
];

const DEFAULT_KEYS = ALL_COLUMNS.filter((c) => c.default).map((c) => c.key);
const VALID_KEYS = new Set(ALL_COLUMNS.map((c) => c.key));

const MonthlyCampaignProfilesResults: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<MonthlyCampaignProfilesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const columnConfigRef = useRef<HTMLDivElement>(null);

  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    const fromUrl = searchParams.get('cols');
    if (fromUrl) {
      const parsed = fromUrl.split(',').map((s) => s.trim()).filter((k) => VALID_KEYS.has(k));
      if (parsed.length) return parsed;
    }
    return DEFAULT_KEYS;
  });

  const fromDate = searchParams.get('from_date') || undefined;
  const toDate = searchParams.get('to_date') || undefined;

  useEffect(() => {
    setLoading(true);
    fetchMonthlyCampaignProfiles({ from_date: fromDate, to_date: toDate })
      .then((d) => { setData(d); setError(null); })
      .catch((e) => { setError(e.message); setData(null); })
      .finally(() => setLoading(false));
  }, [fromDate, toDate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (columnConfigRef.current && !columnConfigRef.current.contains(event.target as Node)) {
        setShowColumnConfig(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleColumn = (key: string) => {
    setVisibleColumns((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      const sp = new URLSearchParams(searchParams);
      const isDefault = next.length === DEFAULT_KEYS.length
        && next.every((k) => DEFAULT_KEYS.includes(k));
      if (isDefault) {
        sp.delete('cols');
      } else {
        sp.set('cols', next.join(','));
      }
      setSearchParams(sp, { replace: true });
      return next;
    });
  };

  const visibleSet = useMemo(() => new Set(visibleColumns), [visibleColumns]);
  const columns = useMemo(
    () => ALL_COLUMNS.filter((c) => visibleSet.has(c.key)),
    [visibleSet],
  );

  const subtitle = data ? `Funded ${data.from_date ?? '—'} to ${data.to_date ?? '—'}` : undefined;

  const headerActions = (
    <div className='relative' ref={columnConfigRef}>
      <button
        type='button'
        onClick={() => setShowColumnConfig((v) => !v)}
        className='inline-flex items-center rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200'
        title='Configure columns'
      >
        <FaCog className='mr-2' size={14} />
        Columns
      </button>
      {showColumnConfig && (
        <div className='absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50'>
          <div className='p-3 border-b border-gray-200'>
            <span className='text-sm font-semibold text-gray-700'>Show/Hide Columns</span>
          </div>
          <div className='max-h-72 overflow-y-auto p-2'>
            {ALL_COLUMNS.map((column) => (
              <label
                key={column.key}
                className='flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer'
              >
                <input
                  type='checkbox'
                  checked={visibleSet.has(column.key)}
                  onChange={() => toggleColumn(column.key)}
                  className='rounded border-gray-300 text-primary focus:ring-primary'
                />
                <span className='text-sm text-gray-700'>{column.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <PredefinedResultsLayout
      title='Monthly Campaign Profiles'
      subtitle={subtitle}
      columns={columns}
      rows={data?.rows ?? []}
      loading={loading}
      error={error}
      rowKey={(r) => r.id}
      onDownloadCsv={() => downloadCsv('monthly-campaign-profiles', searchParams)}
      headerActions={headerActions}
    />
  );
};

export default MonthlyCampaignProfilesResults;
